import Cookies from "js-cookie";

const AUTH_COOKIE = {
  path: "/",
  sameSite: "lax" as const,
  secure:
    typeof window !== "undefined" && window.location.protocol === "https:",
};

export const getStoredAuthToken = (): string | null => {
  return localStorage.getItem("authToken") || Cookies.get("authToken") || null;
};

export const persistAuthSession = (token?: string | null, user?: unknown) => {
  if (!token) {
    return false;
  }
  localStorage.setItem("authToken", token);
  if (user !== undefined) {
    localStorage.setItem("authUser", JSON.stringify(user));
  }
  Cookies.set("authToken", token, AUTH_COOKIE);
  try {
    localStorage.setItem("mito.lastActivityAt", String(Date.now()));
  } catch {
    // ignore
  }
  return true;
};

export const clearAuthSession = () => {
  localStorage.removeItem("authToken");
  localStorage.removeItem("authUser");
  try {
    localStorage.removeItem("mito.lastActivityAt");
  } catch {
    // ignore
  }
  Cookies.remove("authToken", { path: "/" });
};


const PUBLIC_POST_LOGIN_PATHS = new Set([
  "/",
  "/aboutus",
  "/contact",
  "/terms",
  "/privacy",
  "/faq",
  "/FAQ",
]);

/** After login, land on Overview — never the public homepage. */
export const safePostLoginPath = (redirect?: string | null): string => {
  if (!redirect) {
    return "/overview";
  }
  const path = redirect.split("?")[0];
  if (!path.startsWith("/") || path.startsWith("//")) {
    return "/overview";
  }
  if (
    path === "/login" ||
    path.startsWith("/login/") ||
    path === "/signup" ||
    path.startsWith("/signup/") ||
    PUBLIC_POST_LOGIN_PATHS.has(path)
  ) {
    return "/overview";
  }
  return redirect;
};

/**
 * Keep one browser session until logout. Cookie is session-scoped (cleared when
 * the tab/browser closes); localStorage survives reloads and 3DS returns.
 */
export const restoreAuthSession = (): boolean => {
  const token = getStoredAuthToken();
  if (!token) {
    return false;
  }
  persistAuthSession(token);
  return true;
};

const PUBLIC_LOGOUT_KEY = "mito.publicLogout";

export const markPublicLogout = () => {
  try {
    sessionStorage.setItem(PUBLIC_LOGOUT_KEY, "1");
  } catch {
    // Private mode can block sessionStorage; homepage navigation still runs.
  }
};

export const isPublicLogout = (): boolean => {
  try {
    return sessionStorage.getItem(PUBLIC_LOGOUT_KEY) === "1";
  } catch {
    return false;
  }
};

export const clearPublicLogout = () => {
  try {
    sessionStorage.removeItem(PUBLIC_LOGOUT_KEY);
  } catch {
    // ignore
  }
};

/** Leave the signed-in shell and load this tenant’s public homepage. */
export const goToPublicHome = () => {
  markPublicLogout();
  window.location.replace(`${window.location.origin}/`);
};

/** 401 / lost session: stay on homepage after Logout; otherwise go to Login. */
export const redirectAfterLostAuth = () => {
  if (isPublicLogout()) {
    window.location.replace(`${window.location.origin}/`);
    return;
  }
  const path = window.location.pathname || "/";
  window.location.assign(`/login?redirect=${encodeURIComponent(path)}`);
};
